import * as React from 'react';

import { UpdateEvents } from './useSSEEvent';

/**
 * Periodic snapshot poller used as a fallback transport when SSE is unavailable
 * (e.g. corporate networks that block long-lived connections). Refetches on a
 * fixed interval with an in-flight guard so a slow request never overlaps the
 * next tick, and forwards each snapshot through `onMessage` as an
 * {@link UpdateEvents.UPDATE} — keeping the consumer contract identical to SSE.
 */
export const useLongPoll = <T>({
  fetchSnapshot,
  onMessage,
  onError,
  enabled = true,
  intervalMs = 5000,
}: {
  fetchSnapshot: (signal?: AbortSignal) => Promise<T>;
  onMessage: (data: T, eventType: UpdateEvents) => void;
  onError?: (error: string) => void;
  enabled?: boolean;
  intervalMs?: number;
}): void => {
  // Hold callbacks in refs so changing their identity does not restart the
  // polling interval; only `enabled`/`intervalMs` should drive a reset.
  const fetchRef = React.useRef(fetchSnapshot);
  fetchRef.current = fetchSnapshot;
  const onMessageRef = React.useRef(onMessage);
  onMessageRef.current = onMessage;
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  React.useEffect(() => {
    if (!enabled) return;

    let inFlight = false;
    let cancelled = false;
    const controller = new AbortController();

    const poll = async (): Promise<void> => {
      if (inFlight) return;
      inFlight = true;
      try {
        const data = await fetchRef.current(controller.signal);
        if (!cancelled) {
          onMessageRef.current(data, UpdateEvents.UPDATE);
        }
      } catch (error: unknown) {
        // A cleanup-triggered abort surfaces here; the `cancelled` guard keeps
        // it from reaching the consumer as a spurious error.
        if (!cancelled) {
          const message =
            error instanceof Error ? error.message : 'Unknown error';
          onErrorRef.current?.(message);
        }
      } finally {
        inFlight = false;
      }
    };

    void poll();
    const intervalId = setInterval(() => void poll(), intervalMs);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(intervalId);
    };
  }, [enabled, intervalMs]);
};
